import { BTN_TXT, DOC_TYPE_FRIENDLY_NAMES } from "../../constants/constants";
import {
    capitalizeWords,
    handleUploadFile,
    validateFinancialTemplate,
    getDownloadSuccessMessage,
} from "../../helpers/helpers";
import {
  Card,
  Typography,
  Dropdown,
  Button,
  Upload,
  App,
  Modal,
  Flex,
  Tooltip,
} from "antd";
import {
  DownOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  uploadFinancialMetrics,
  document as fetchDocuments,
  uploadOtherDocuments,
  getFinancialMetrics,
} from "../../redux/slices/DocumentSlice";
import { useState } from "react";
import { useDispatch } from "react-redux";
import financialEvaluation from "../../assets/docs/financial_eval.xlsx";
import ratios from "../../assets/docs/ratios.xlsx";
import financialSummary from "../../assets/docs/financial_summary.xlsx";
import FinancialMetricsPreviewModal from "./FinancialMetricsPreviewModal";
import "./CustomCard.scss";

const { Text } = Typography;

const SAMPLE_FILES = {
  financialEvaluation,
  ratios,
  financialSummary,
};

const DocumentCard = ({
  doc,
  docType,
  orgId,
  entId,
  busEntId,
  dealCategoryId,
}) => {
  const { message } = App.useApp();
  const dispatch = useDispatch();

  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [uploadingMap, setUploadingMap] = useState({});
  const [sampleDownloaded, setSampleDownloaded] = useState(false);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsData, setMetricsData] = useState(null);

  const isMissing = doc?.isMissing;

  const docName =
    docType === "INVOICE"
      ? `Invoice_${doc.dealId}`
      : DOC_TYPE_FRIENDLY_NAMES[doc.docMasterId] || doc.docMasterName;

  const allowUpload =
    docName?.toLowerCase().includes("financial evaluation") ||
    docName?.toLowerCase().includes("ratios") ||
    docName?.toLowerCase().includes("financial summary");

  const otherDocs = docName?.toLowerCase().includes("other documents");

  const getUploadKey = () => {
    const name = docName.toLowerCase();
    if (name.includes("financial evaluation")) return "financialEvaluation";
    if (name.includes("ratios")) return "ratios";
    if (name.includes("financial summary")) return "financialSummary";
    return null;
  };

  const uploadKey = getUploadKey();

  const handleSampleDownload = () => {
    const sampleUrl = SAMPLE_FILES[uploadKey];

    const link = document.createElement("a");
    link.href = sampleUrl;
    link.download = sampleUrl.split("/").pop();

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSampleDownloaded(true);
  };

  const handleDownload = (url, fileName = "document") => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(getDownloadSuccessMessage(fileName));
  };

  const openPdfModal = (url) => {
    setPdfUrl(url);
    setIsPdfModalOpen(true);
  };

  const openMetricsPreview = async () => {
    try {
      setIsMetricsModalOpen(true);
      setMetricsLoading(true);
      setMetricsData(null);

      const result = await dispatch(
        getFinancialMetrics({
          orgId: docType === "BUSINESS" ? orgId : undefined,
          entId: docType === "ENTERPRISE" ? entId : undefined,
        })
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        setMetricsData(result.payload || {});
      } else {
        const payloadError = result?.payload;
        const errorMessage =
          (typeof payloadError === "string" && payloadError) ||
          payloadError?.error ||
          payloadError?.message ||
          "Unable to load financial metrics";
        message.error(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        (typeof err === "string" && err) ||
        err?.message ||
        "Unable to load financial metrics";
      message.error(errorMessage);
    } finally {
      setMetricsLoading(false);
    }
  };

  const beforeUpload = async (file) => {
    const isLt20MB = file.size / 1024 / 1024 < 20;

    if (!isLt20MB) {
      message.error("File must be smaller than 20MB");
      return Upload.LIST_IGNORE;
    }

    if (otherDocs) {
      const isValid =
        file.type === "application/pdf" ||
        file.type === "application/zip" ||
        file.name.toLowerCase().endsWith(".pdf") ||
        file.name.toLowerCase().endsWith(".zip");

      if (!isValid) {
        message.error("Please upload PDF or ZIP file only");
        return Upload.LIST_IGNORE;
      }

      return true;
    }

    const isXlsx =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");

    if (!isXlsx) {
      message.error("Please upload XLSX file only");
      return Upload.LIST_IGNORE;
    }

    if (allowUpload) {
      const validation = await validateFinancialTemplate(file, uploadKey);

      if (!validation.valid) {
        message.error(validation.message);
        return Upload.LIST_IGNORE;
      }
    }

    return true;
  };

  const onUploadClick = async (file, uploadKey) => {
    try {
      const path =
        docType === "ENTERPRISE" ? `enterprise/${entId}` : `business/${orgId}`;

      setUploadingMap((p) => ({ ...p, [uploadKey]: true }));

      const cleanFile = new File([file], file.name.replace(/\s+/g, "_"), {
        type: file.type,
      });

      const { fileUrl } = await handleUploadFile(cleanFile, path);

      const payload = {
        entId: docType === "ENTERPRISE" ? entId : undefined,
        orgId: docType === "BUSINESS" ? orgId : undefined,
        urls: { [uploadKey]: fileUrl },
        isReupload: !isMissing,
        dealCategoryId: docType === "BUSINESS" ? dealCategoryId : undefined,
      };

      let result;
      if (docType === "BUSINESS" && otherDocs) {
        result = await dispatch(
          uploadOtherDocuments({ orgId, url: fileUrl, dealCategoryId }),
        );
      } else {
        result = await dispatch(uploadFinancialMetrics(payload));
      }

      if (result?.payload?.statusCode === 201) {
        dispatch(
          fetchDocuments({
            type: docType,
            entId: docType === "ENTERPRISE" ? entId : undefined,
            orgId: docType === "BUSINESS" ? orgId : undefined,
            dealCategoryId: docType === "BUSINESS" ? dealCategoryId : undefined,
          }),
        );
        message.success("File Uploaded successfully");
      } else {
        const payloadError = result?.payload;
        const errorMessage =
          (typeof payloadError === "string" && payloadError) ||
          payloadError?.error ||
          payloadError?.message ||
          "File upload failed";
        message.error(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        (typeof err === "string" && err) ||
        err?.message ||
        "File upload failed";
      message.error(errorMessage);
    } finally {
      setUploadingMap((p) => ({ ...p, [uploadKey]: false }));
    }
  };

  const uploadProps = {
    beforeUpload,
    showUploadList: false,
    accept: allowUpload ? ".xlsx" : ".pdf,.zip",
    customRequest: async ({ file, onSuccess, data }) => {
      await onUploadClick(file, data.uploadKey);
      onSuccess("ok");
    },
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "";

    try {
      const fileName = decodeURIComponent(url.split("/").pop()?.split("?")[0]);

      return fileName;
    } catch {
      return "";
    }
  };

  return (
    <Card className="doc-card" style={{ height: "100%" }}>
      <Flex justify="space-between" align="center">
        <Text strong>{capitalizeWords(docName)}</Text>

        {docType === "INVOICE" ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: "csv",
                  label: "Download CSV",
                  onClick: () =>
                    handleDownload(doc.csvFileUrl, `${docName}.csv`),
                },
                {
                  key: "zip",
                  label: "Download ZIP",
                  onClick: () =>
                    handleDownload(doc.invoiceFileUrl, `${docName}.zip`),
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} />
          </Dropdown>
        ) : (docType === "ENTERPRISE" || docType === "BUSINESS") &&
          allowUpload &&
          uploadKey ? (
          isMissing ? (
            <div style={{ display: "flex", gap: 8 }}>
              {!sampleDownloaded && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSampleDownload}
                >
                  Download Sample
                </Button>
              )}

              {sampleDownloaded && (
                <Upload {...uploadProps} data={{ uploadKey }}>
                  <Button loading={uploadingMap[uploadKey]} type="primary">
                    Upload
                  </Button>
                </Upload>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Tooltip title="View">
                <Button
                color="primary"
                  variant="outlined"
                  icon={<EyeOutlined />}
                  onClick={openMetricsPreview}
                  aria-label="View financial metrics"
                />
              </Tooltip>

              <Tooltip title="Download">
                <Button
                color="primary"
                  variant="outlined"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(
                      doc.docUrl,
                      getFileNameFromUrl(doc.docUrl) || docName
                    )
                  }
                  aria-label="Download financial document"
                />
              </Tooltip>

              <Upload {...uploadProps} data={{ uploadKey }}>
                <Button loading={uploadingMap[uploadKey]} type="primary">
                  {BTN_TXT.REUPLOAD}
                </Button>
              </Upload>
            </div>
          )
        ) : docType === "BUSINESS" && otherDocs ? (
          isMissing ? (
            <Upload {...uploadProps} data={{ uploadKey: "others" }}>
              <Button loading={uploadingMap.others} type="primary">
                Upload
              </Button>
            </Upload>
          ) : (
            <Button
              variant="outlined"
              color="default"
              onClick={() => handleDownload(doc.docUrl)}
            >
              Download
            </Button>
          )
        ) : (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => openPdfModal(doc.docUrl)}
          >
            View
          </Button>
        )}
      </Flex>
      <Text type="secondary">{getFileNameFromUrl(doc?.docUrl)}</Text>
      <Modal
        open={isPdfModalOpen}
        onCancel={() => setIsPdfModalOpen(false)}
        footer={null}
        width={900}
        centered
      >
        <iframe
          src={pdfUrl}
          title="Document"
          width="100%"
          height="600"
          style={{ border: "none" }}
        />
      </Modal>

      <FinancialMetricsPreviewModal
        open={isMetricsModalOpen}
        onClose={() => setIsMetricsModalOpen(false)}
        loading={metricsLoading}
        metricsData={metricsData}
        uploadKey={uploadKey}
      />
    </Card>
  );
};

export default DocumentCard;
